import React from "react"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { useTranslation } from "react-i18next"
import { IProductRequest } from "../../Interfaces/IProductRequest"

import { IPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { EProductOrderModeType } from "../../Enum/EProductOrderModeType"
import { toReservationModeLabel } from "../../Extensions/ReservationExtensions"
import ProductImageCarousel from "./ProductImageCarousel"

interface MenuCardProps {
  product: IProductRequest
  onAddToCart: (p: IProductRequest) => void
  onViewDetails?: (p: IProductRequest) => void
  mode: EOrderPlaceType
  isOpen: boolean
  theme: IPublicMenuTheme
  canOrderDirectly?: boolean
  isViewOnly?: boolean
  isRodizioIncluded?: boolean
}

const MenuCard: React.FC<MenuCardProps> = ({
  product,
  onAddToCart,
  onViewDetails,
  mode,
  isOpen,
  theme,
  canOrderDirectly,
  isViewOnly = false,
  isRodizioIncluded = false,
}) => {
  const isRodizioPlanProduct = Boolean(product.isRodizioPlanProduct)
  const { t } = useTranslation()

  const hasPromotion = !isRodizioIncluded && typeof product.oldPrice === "number" && product.oldPrice > product.price

  const discountPercent = hasPromotion ? Math.round(((product.oldPrice! - product.price) / product.oldPrice!) * 100) : 0

  const hasAddOns = product.availableAddOns && product.availableAddOns.length > 0

  const hasPaidAddOns = product.availableAddOns?.some((addOn) => Number(addOn.price) > 0) ?? false

  const hasPaidSelectionOptions =
    product.availableSelections?.some((selection) =>
      selection.options.some((option) => !option.isFree && Number(option.value) > 0),
    ) ?? false

  const shouldShowFromPrice = !isRodizioIncluded && (hasPaidAddOns || hasPaidSelectionOptions)

  const hasComboItemsPreview = (product.comboItemsPreview?.length ?? 0) > 0

  const canReserve =
    product.productOrderMode === EProductOrderModeType.ReservationOnly ||
    product.productOrderMode === EProductOrderModeType.ImmediateAndReservation

  const reservationModeLabel = toReservationModeLabel(product.productOrderMode)

  const colors = [
    "bg-emerald-500 text-white border-emerald-400",
    "bg-orange-500 text-white border-orange-400",
    "bg-blue-500 text-white border-blue-400",
  ]

  const getBadgeStyles = (badge: string) => {
    const index = badge.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length
    return colors[index]
  }

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const shouldAllowDirectOrder =
    !isViewOnly && !isRodizioPlanProduct && (canOrderDirectly ?? mode === EOrderPlaceType.Delivery)

  const askWaiterButtonColor = isLegacyTheme ? "#DC2626" : theme.buttonColor || "#DC2626"

  const askWaiterButtonTextColor = isLegacyTheme ? "#FFFFFF" : theme.buttonTextColor || "#FFFFFF"

  const handleViewDetails = () => (onViewDetails ?? onAddToCart)(product)

  const comboPreview = hasComboItemsPreview ? (
    <div className="mt-2 rounded-lg bg-black/5 px-2 py-2">
      <p className="text-[10px] font-black uppercase tracking-[0.15em] opacity-70">
        {t("publicMenu:add_meal.combo_items")}
      </p>

      <ul className="mt-1 space-y-1">
        {product.comboItemsPreview!.slice(0, 4).map((comboItem) => (
          <li key={comboItem} className="line-clamp-1 text-[11px] font-semibold opacity-80">
            {comboItem}
          </li>
        ))}
      </ul>
    </div>
  ) : null

  const hasRodizioSubProducts = isRodizioPlanProduct && (product.rodizioSubProductNames?.length ?? 0) > 0

  const rodizioSubProductsPreview = hasRodizioSubProducts ? (
    <div className="mt-2 rounded-lg bg-black/5 px-2 py-2">
      <p className="text-[10px] font-black uppercase tracking-[0.15em] opacity-70">
        {t("rodizio:public.plan_includes")}
      </p>

      <ul className="mt-1 space-y-1">
        {product.rodizioSubProductNames!.slice(0, 4).map((subProductName) => (
          <li key={subProductName} className="line-clamp-1 text-[11px] font-semibold opacity-80">
            🍽️ {subProductName}
          </li>
        ))}
      </ul>
    </div>
  ) : null

  if (isLegacyTheme && theme.cardLayout === "cover") {
    return (
      <div
        className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_60px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-700 flex flex-col h-full relative"
        onClick={handleViewDetails}
        data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.handleViewDetails"
      >
        <div className="relative aspect-4/3 overflow-hidden bg-white">
          <ProductImageCarousel product={product} theme={theme} />

          {hasPromotion && (
            <div className="absolute top-4 left-4 z-10">
              <div className="bg-red-600 text-white px-3 py-1 rounded-xl shadow-lg border border-red-500 -rotate-2">
                <span className="text-[10px] font-black tracking-tight">{discountPercent}% OFF</span>
              </div>
            </div>
          )}

          <div className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg border border-white/50 text-lg group-hover:rotate-12 transition-transform">
            {product.categoryIcon || "🍴"}
          </div>

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>

        <div className="p-5 flex flex-col flex-1">
          {product.badges && product.badges.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {product.badges.map((badge) => (
                <span
                  key={badge}
                  className={`px-2 py-0.5 rounded-lg text-[11px] font-black uppercase tracking-widest border ${getBadgeStyles(badge)}`}
                >
                  {badge}
                </span>
              ))}
            </div>
          )}

          <h3 className="text-base font-black text-gray-900 leading-tight uppercase tracking-tight group-hover:text-orange-600 transition-colors">
            {product.name}
          </h3>

          <p className="text-[11px] text-gray-500 leading-relaxed mt-1 line-clamp-2 flex-1">{product.description}</p>

          {product.hasPreparationTime && (product.preparationTimeMinutes ?? 0) > 0 && (
            <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
              ⏱️ {t("publicMenu:menu_card.preparation_time", { minutes: product.preparationTimeMinutes })}
            </p>
          )}

          <p
            className={`mt-2 text-[10px] font-black uppercase tracking-widest ${canReserve ? "font-bold text-red-700" : "text-slate-500"}`}
          >
            ⚡ • {reservationModeLabel}
          </p>

          {comboPreview}

          {rodizioSubProductsPreview}

          {hasAddOns && (
            <div className="flex items-center gap-1.5 mb-0 group/addons my-2">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.15em] text-orange-600">
                {t("publicMenu:menu_card.customizable")}
              </span>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-col">
              {hasPromotion && (
                <span className="text-[10px] text-gray-400 line-through">R$ {product.oldPrice?.toFixed(2)}</span>
              )}

              {shouldShowFromPrice && (
                <span className="mb-0.5 text-[9px] font-black uppercase tracking-[0.15em] text-gray-500">
                  {t("publicMenu:menu_card.from_price")}
                </span>
              )}

              {isRodizioIncluded ? (
                <span className="text-sm font-black uppercase tracking-wide text-emerald-700">
                  {t("rodizio:public.included")}
                </span>
              ) : (
                <div className="flex items-baseline gap-0.5">
                  <span className={`text-[10px] font-black ${hasPromotion ? "text-red-600" : "text-orange-600"}`}>
                    R$
                  </span>

                  <span
                    className={`text-2xl font-black tracking-tighter leading-none ${
                      hasPromotion ? "text-red-600" : "text-gray-900"
                    }`}
                  >
                    {Math.floor(product.price)}

                    <span className="text-sm opacity-80">,{(product.price % 1).toFixed(2).substring(2)}</span>
                  </span>
                </div>
              )}
            </div>

            {isViewOnly ? null : isOpen ? (
              shouldAllowDirectOrder ? (
                <button
                  onClick={(event) => {
                    event.stopPropagation()
                    onAddToCart(product)
                  }}
                  className="group/btn relative flex items-center justify-center h-11 px-3 bg-emerald-500 hover:bg-orange-600 text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-orange-200 active:scale-95 overflow-hidden"
                  data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event"
                >
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] mr-1">
                    {t("publicMenu:menu_card.add")}
                  </span>

                  <div className="w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center group-hover/btn:bg-white/20 transition-colors">
                    <svg
                      className="w-3.5 h-3.5 group-hover/btn:rotate-90 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>

                  <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover/btn:left-full transition-all duration-700" />
                </button>
              ) : (
                <button
                  onClick={(event) => {
                    event.stopPropagation()
                    handleViewDetails()
                  }}
                  className="ml-5 px-5 py-1 text-[16px] font-black leading-tight uppercase rounded-lg border text-center"
                  style={{
                    backgroundColor: askWaiterButtonColor,
                    color: askWaiterButtonTextColor,
                    borderColor: isLegacyTheme ? "#b91c1c" : theme.borderColor,
                    borderWidth: theme.borderThickness,
                  }}
                  data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event2"
                >
                  {t("publicMenu:menu_card.ask_waiter")}
                </button>
              )
            ) : (
              <div className="ml-5 px-5 py-1 bg-red-50 ring-2 ring-red-500 text-[16px] font-black text-gray-900 uppercase rounded-lg border border-gray-200">
                Estamos fechados
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const isCompact = theme.cardLayout !== "cover"

  const imageOnLeft = theme.cardLayout !== "compact-right"

  const hasHighlightedPromotion = hasPromotion && theme.highlightPromotions

  return (
    <div
      className={`group relative mx-auto w-full overflow-hidden border shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(0,0,0,0.12)] ${
        isCompact ? "max-w-120 rounded-[1.25rem] p-3" : "rounded-2xl"
      }`}
      onClick={handleViewDetails}
      style={{
        backgroundColor: theme.surfaceColor,
        borderColor: theme.borderColor,
        borderWidth: theme.borderThickness,
        color: theme.textColor,
      }}
      data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.handleViewDetails2"
    >
      {isCompact ? (
        <div className={`flex items-start gap-3 ${imageOnLeft ? "flex-row" : "flex-row-reverse"}`}>
          <div
            className="relative mt-1 h-24 w-24 shrink-0 self-start overflow-hidden rounded-2xl"
            style={{ backgroundColor: "#FFFFFF" }}
          >
            <ProductImageCarousel product={product} compact theme={theme} />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex w-full items-start justify-between gap-2">
              <div className="min-w-0 w-full">
                <h3 className="truncate text-sm font-black uppercase tracking-tight">{product.name}</h3>
                <p className="mt-1 line-clamp-2 text-[11px] opacity-70">{product.description}</p>
                {product.hasPreparationTime && (product.preparationTimeMinutes ?? 0) > 0 && (
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                    ⏱️ {t("publicMenu:menu_card.preparation_time", { minutes: product.preparationTimeMinutes })}
                  </p>
                )}

                <p
                  className={`mt-1 text-[10px] font-black uppercase tracking-widest ${canReserve ? "font-bold text-red-700" : "text-slate-500"}`}
                >
                  ⚡ • {reservationModeLabel}
                </p>

                {comboPreview}

                {rodizioSubProductsPreview}

                {product.badges && product.badges.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1 mt-2">
                    {product.badges.map((badge) => (
                      <span
                        key={badge}
                        className={`rounded-lg border px-2 py-0.5 text-[8px] font-black uppercase tracking-widest ${getBadgeStyles(badge)}`}
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="shrink-0 rounded-xl border px-2 py-1 text-sm"
                style={{
                  borderColor: theme.borderColor,
                  borderWidth: theme.borderThickness,
                  backgroundColor: theme.secondaryColor,
                }}
              >
                {product.categoryIcon || "🍴"}
              </div>
            </div>

            <div className="mt-3 flex w-full items-center justify-between gap-2">
              <div>
                {hasPromotion && (
                  <span className="text-[10px] opacity-40 line-through">R$ {product.oldPrice?.toFixed(2)}</span>
                )}

                {shouldShowFromPrice && (
                  <span className="block text-[9px] font-black uppercase tracking-[0.15em] opacity-60">
                    {t("publicMenu:menu_card.from_price")}
                  </span>
                )}

                {isRodizioIncluded ? (
                  <p className="text-sm font-black uppercase tracking-wide" style={{ color: theme.primaryColor }}>
                    {t("rodizio:public.included")}
                  </p>
                ) : (
                  <p
                    className="text-lg font-black"
                    style={{ color: hasPromotion ? theme.accentColor : theme.primaryColor }}
                  >
                    R$ {product.price.toFixed(2).replace(".", ",")}
                  </p>
                )}
              </div>

              {hasHighlightedPromotion && (
                <span
                  className="rounded-xl px-2 py-1 text-[9px] font-black text-white"
                  style={{ backgroundColor: theme.accentColor }}
                >
                  {discountPercent}% OFF
                </span>
              )}
            </div>

            {hasAddOns && (
              <p
                className="mt-2 text-[10px] font-black uppercase tracking-[0.15em]"
                style={{ color: theme.accentColor }}
              >
                {t("publicMenu:menu_card.customizable")}
              </p>
            )}

            <div className={`mt-2 flex w-full ${imageOnLeft ? "justify-end" : "justify-start"}`}>
              {isViewOnly ? null : isOpen ? (
                shouldAllowDirectOrder ? (
                  <button
                    onClick={(event) => {
                      event.stopPropagation()
                      onAddToCart(product)
                    }}
                    className="rounded-[0.8rem] px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-white transition-transform active:scale-95"
                    style={{ backgroundColor: theme.buttonColor, color: theme.buttonTextColor }}
                    data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event3"
                  >
                    {t("publicMenu:menu_card.add")}
                  </button>
                ) : (
                  <button
                    onClick={(event) => {
                      event.stopPropagation()
                      handleViewDetails()
                    }}
                    className="rounded-[0.8rem] px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em]"
                    style={{
                      backgroundColor: askWaiterButtonColor,
                      color: askWaiterButtonTextColor,
                      borderColor: theme.borderColor,
                      borderWidth: theme.borderThickness,
                    }}
                    data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event4"
                  >
                    {t("publicMenu:menu_card.ask_waiter")}
                  </button>
                )
              ) : (
                <div
                  className="rounded-[0.8rem] px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-white"
                  style={{ backgroundColor: "#DC2626" }}
                >
                  Estamos fechados
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <div
            className="relative aspect-4/3 overflow-hidden"
            style={{
              backgroundColor: "#FFFFFF",

              borderWidth: theme.borderThickness,
            }}
          >
            <ProductImageCarousel product={product} theme={theme} />

            {hasHighlightedPromotion && (
              <div
                className="absolute left-4 top-4 z-10 rounded-xl px-3 py-1 text-white shadow-lg -rotate-2"
                style={{ backgroundColor: theme.accentColor }}
              >
                <span className="text-[10px] font-black tracking-tight">{discountPercent}% OFF</span>
              </div>
            )}

            <div
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl border text-lg"
              style={{
                backgroundColor: theme.surfaceColor,
                borderColor: theme.borderColor,
                borderWidth: theme.borderThickness,
              }}
            >
              {product.categoryIcon || "🍴"}
            </div>
          </div>

          <div className="flex flex-1 flex-col p-5">
            {product.badges && product.badges.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1 mt-2">
                {product.badges.map((badge) => (
                  <span
                    key={badge}
                    className={`rounded-lg border px-2 py-0.5 text-[11px] font-black uppercase tracking-widest ${getBadgeStyles(badge)}`}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}

            <h3 className="text-base font-black uppercase tracking-tight">{product.name}</h3>

            <p className="mt-1 line-clamp-2 flex-1 text-[11px] opacity-70">{product.description}</p>

            <p
              className={`mt-2 text-[10px] font-black uppercase tracking-widest ${canReserve ? "font-bold text-red-700" : "text-slate-500"}`}
            >
              ⚡ • {reservationModeLabel}
            </p>

            {comboPreview}
            
            {rodizioSubProductsPreview}
           
            {hasAddOns && (
              <p
                className="my-2 text-[10px] font-black uppercase tracking-[0.15em]"
                style={{ color: theme.accentColor }}
              >
                {t("publicMenu:menu_card.customizable")}
              </p>
            )}

            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-col">
                {hasPromotion && (
                  <span className="text-[10px] opacity-40 line-through">R$ {product.oldPrice?.toFixed(2)}</span>
                )}

                {shouldShowFromPrice && (
                  <span className="mb-0.5 text-[9px] font-black uppercase tracking-[0.15em] opacity-60">
                    {t("publicMenu:menu_card.from_price")}
                  </span>
                )}

                {isRodizioIncluded ? (
                  <span className="text-sm font-black uppercase tracking-wide" style={{ color: theme.primaryColor }}>
                    {t("rodizio:public.included")}
                  </span>
                ) : (
                  <span
                    className="text-2xl font-black leading-none tracking-tighter"
                    style={{ color: hasPromotion ? theme.accentColor : theme.primaryColor }}
                  >
                    R$ {product.price.toFixed(2).replace(".", ",")}
                  </span>
                )}
              </div>

              {isViewOnly ? null : isOpen ? (
                shouldAllowDirectOrder ? (
                  <button
                    onClick={(event) => {
                      event.stopPropagation()
                      onAddToCart(product)
                    }}
                    className="rounded-[0.7rem] px-3 py-3 text-[9px] font-black uppercase tracking-[0.2em] text-white transition-transform active:scale-95"
                    style={{ backgroundColor: theme.buttonColor, color: theme.buttonTextColor }}
                    data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event5"
                  >
                    {t("publicMenu:menu_card.add")}
                  </button>
                ) : (
                  <button
                    onClick={(event) => {
                      event.stopPropagation()
                      handleViewDetails()
                    }}
                    className="rounded-[0.7rem] px-3 py-2 text-[11px] font-black uppercase"
                    style={{
                      backgroundColor: askWaiterButtonColor,
                      color: askWaiterButtonTextColor,
                      borderColor: theme.borderColor,
                      borderWidth: theme.borderThickness,
                    }}
                    data-interaction-description-key="interactionDescriptions:publicMenu.components.menuCard.button.event6"
                  >
                    {t("publicMenu:menu_card.ask_waiter")}
                  </button>
                )
              ) : (
                <div
                  className="rounded-[0.7rem] px-3 py-2 text-[11px] font-black uppercase text-white"
                  style={{ backgroundColor: "#DC2626" }}
                >
                  Estamos fechados
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuCard
